import { Box, Breadcrumbs } from '@mui/material';
import { subscribe, unsubscribe } from 'pubsub-js';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { NavigationSource } from '../../common/enums/NavigationSource';
import { NewViewTypeEnum } from "../../common/enums/NewViewTypeEnum";
import { UIViewEnum } from '../../common/enums/UIViewEnum';
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { OBJECT_CHANGED_TOPIC, ObjectChangedMsg } from '../../common/models/pub-sub/PubSubTopics';
import { getCustomizationViewCategorySettings } from "../../common/utilities/GetCustomizationViewCategorySettings";
import { getParentViewIDFromPath } from '../../common/utilities/RouteHelpers';
import { LayoutContextActionType, SwipeActionEnum } from "../../contexts/layout-context/LayoutContextTypes";
import { useLayoutContext } from "../../contexts/layout-context/UseLayoutContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useRouteHistory } from "../../contexts/route-history-context/UseRouteHistory";
import { useUserContext } from '../../contexts/user-context/UserContext';
import useNavigateToView from '../../hooks/use-navigate-to-view/UseNavigateToView';
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from '../../queries/UseQueryNotifications';
import { useBreadcrumb } from '../../queries/breadcrumb/UseBreadcrumb';
import { useUpdateBreadCrumb } from '../../queries/breadcrumb/UseUpdateBreadcrumb';
import { useUICustomizationSettings } from "../../queries/ui-customization/UseUICustomizationSettings";
import { IRoute, routes, UIViewCategory } from "../../routes/routes";
import BreadCrumbObjectItem from "./BreadCrumbObjectItem";
import BreadCrumbViewItem from "./BreadCrumbViewItem";

export default function BreadCrumbV2Desktop() {
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [layoutState, layoutDispatch] = useLayoutContext();
    const location = useLocation();
    const [offlineState] = useOfflineDetectContext();
    const [routeHistory] = useRouteHistory();
    const navigateToView = useNavigateToView();

    const viewID = getParentViewIDFromPath(location.pathname, UIViewEnum.Group_SummaryListing_Devices);

    const { data: response, refetch } = useQueryNotifications(useBreadcrumb)({
        customerID: userState.customer?.id as number,
        viewID: viewID,
        routeContext: routeContext,
        offlineDataLoaded: offlineState.offlineDataLoaded,
        offline: offlineState.offline
    });

    const { mutate: updateBreadCrumb } = useQueryNotifications(useUpdateBreadCrumb)({
        customerID: userState.customer?.id as number,
        viewID: viewID,
        routeContext: routeContext,
        offlineDataLoaded: offlineState.offlineDataLoaded,
        offline: offlineState.offline
    });

    const {
        data: customizationSettings
    } = useQueryNotifications(useUICustomizationSettings)({
        userID: userState.user?.id as number,
        enabled: true
    });


    function getDefaultRoute(viewCategory: UIViewCategory | undefined): IRoute | undefined {
        return routes.find(r => r.category === viewCategory && r.isCategoryDefault);
    }

    function getViewCategory(objectTypeID: eLynxObjectTypeEnum): UIViewCategory | undefined {
        return objectTypeID === eLynxObjectTypeEnum.Group
            ? UIViewCategory.Group
            : objectTypeID === eLynxObjectTypeEnum.Well
                ? UIViewCategory.Well
                : objectTypeID === eLynxObjectTypeEnum.Device
                    ? UIViewCategory.Device
                    : objectTypeID === eLynxObjectTypeEnum.TankBattery
                        ? UIViewCategory.TankBattery
                        : objectTypeID === eLynxObjectTypeEnum.Tank
                            ? UIViewCategory.Tank
                            : objectTypeID === eLynxObjectTypeEnum.Tag
                                ? UIViewCategory.Tag
                                : undefined;
    }

    useEffect(() => {

        if (response && layoutState.swipeAction !== undefined &&
            layoutState.swipeAction !== SwipeActionEnum.None &&
            (response?.relatedItems.length ?? 0) > 1) {

            const ndx = response.relatedItems.findIndex(o => o.objectID === routeContext.contextObject?.id);
            let targetIndex: number = -1;

            if (layoutState.swipeAction === SwipeActionEnum.Left) {
                // next
                targetIndex = (ndx === response.relatedItems.length - 1 ? 0 : ndx + 1);

            } else if (layoutState.swipeAction === SwipeActionEnum.Right) {
                // previous
                targetIndex = (ndx === 0 ? response.relatedItems.length - 1 : ndx - 1);
            }

            navigateToView(
                response.relatedItems[targetIndex].objectTypeID,
                response.relatedItems[targetIndex].objectID,
                undefined,
                layoutState.swipeAction === SwipeActionEnum.Left
                    ? NavigationSource.ArrowPrev
                    : NavigationSource.ArrowNext
            );

            layoutDispatch({
                type: LayoutContextActionType.SwipeChanged,
                payload: undefined
            });
        }
    }, [
        layoutState.swipeAction,
        response,
        routeContext.contextObject,
        navigateToView,
        layoutDispatch
    ]);

    useEffect(() => {
        const onObjectChanged = (objectChangedData: ObjectChangedMsg) => {

            const item = response?.breadcrumbItems.find(o => o.objectID === objectChangedData.entity.id && o.objectTypeID === objectChangedData.entity.objectType);

            if (item) {

                // the changed object is a group and the name is not set, then the change likely came from
                // group admin so rebuild the breadcrumb
                if (objectChangedData.entity.objectType === eLynxObjectTypeEnum.Group &&
                    objectChangedData.name === undefined) {

                    refetch();

                    return;
                }

                if (objectChangedData.name) {
                    item.title = objectChangedData.name;
                }

                if (objectChangedData.visible !== undefined) {
                    item.isHidden = objectChangedData.visible ? false : true;
                }

                if (objectChangedData.disabled !== undefined) {
                    item.isObjectDisabled = objectChangedData.disabled ? true : false;
                }

                updateBreadCrumb(item);
            }
        }

        const token = subscribe(OBJECT_CHANGED_TOPIC, (message, data) => onObjectChanged(data));

        return () => {
            if (token) {
                unsubscribe(token);
            }
        }
    }, [
        response,
        updateBreadCrumb,
        refetch
    ]);

    const currentGroupIndex = response?.breadcrumbItems.findIndex(o => o.objectID === routeContext.groupID && o.objectTypeID === eLynxObjectTypeEnum.Group);
    const breadcrumbItems = response?.breadcrumbItems.slice(currentGroupIndex)
    const breadcrumbsElement = useRef<HTMLDivElement>(null);

    // Automatically adjust breadcrumb item widths based on available space
    useEffect(() => {
        const scaleAdjustmentFactor = 20;
        const minWidth = 42;
        const parent = breadcrumbsElement.current?.getElementsByTagName("ol")[0];

        let resizeObserver: ResizeObserver | undefined;

        if (parent) {
            // Re-run the width adjustment logic on resize
            resizeObserver = new ResizeObserver(() => {
                const segments = parent?.querySelectorAll("li.MuiBreadcrumbs-li");
                const separators = parent?.querySelectorAll("li.MuiBreadcrumbs-separator");
                const segmentCount = segments?.length ?? 0;

                if (segmentCount > 0) {
                    // Reset any previous max-width
                    Array.from(segments ?? []).forEach(segment => {
                        segment.removeAttribute("style");
                    });

                    // Calculate total available width
                    const separatorWidth = Array.from(separators ?? []).reduce((sum, separator) => sum + separator.clientWidth, 0);
                    const availableWidth = parent.offsetWidth - separatorWidth;

                    // Get the natural widths of each item
                    const segmentWidths = Array.from(segments ?? []).map(segment => segment.clientWidth);

                    // Calculate the total width of all breadcrumb items
                    const totalSegmentWidth = segmentWidths.reduce((sum, width) => sum + width, 0);
                    const overflow = totalSegmentWidth - availableWidth;

                    if (overflow > 0) {
                        // Shrink items > avgWidth 20 times faster than <= avgWidth
                        const avgWidth = totalSegmentWidth / segmentCount;
                        const countLE = segmentWidths.filter(w => w <= avgWidth).length;
                        const countGT = segmentWidths.length - countLE;

                        // If all are <= avg or all > avg, avoid division by zero
                        let x = 0;
                        if (countLE + scaleAdjustmentFactor * countGT > 0) {
                            x = overflow / (countLE + scaleAdjustmentFactor * countGT);
                        }

                        // Calculate new widths based on shrink factor
                        const newWidths = segmentWidths.map(width => {
                            if (width > avgWidth) {
                                return Math.max(minWidth, width - scaleAdjustmentFactor * x);
                            } else {
                                return Math.max(minWidth, width - x);
                            }
                        });

                        // Apply the new widths as max-width in pixels
                        newWidths.forEach((width, index) => {
                            const percent = (width / availableWidth) * 100;
                            (segments as NodeListOf<HTMLElement>)[index].setAttribute("style", `max-width: ${percent}%; overflow: hidden;`);
                        });
                    }
                }
            });
            resizeObserver.observe(parent);
        }

        return () => {
            if (resizeObserver && parent) {
                resizeObserver.unobserve(parent);
                resizeObserver.disconnect();
            }
        };
    }, []);

    return (
        <Box
            sx={{
                display: 'flex',
                flex: '1 1 auto',
                alignItems: 'flex-end',
                minHeight: theme => theme.spacing(6),
                overflow: "hidden"
            }}
        >
            <Breadcrumbs
                ref={breadcrumbsElement}
                aria-label='breadcrumb'
                sx={{
                    fontSize: '.9rem',
                    "& ol": {
                        alignItems: "flex-end",
                        overflow: "hidden",
                        flexWrap: "nowrap"
                    },
                    "& .MuiBreadcrumbs-separator": {
                        margin: 0,
                        paddingRight: 1
                    },
                    flex: "1 1 auto",
                    overflow: "hidden"
                }}
            >
                {
                    breadcrumbItems?.map((item, index) => {

                        const settings = getCustomizationViewCategorySettings(
                            item.objectTypeID,
                            customizationSettings
                        );

                        const viewCategory = getViewCategory(item.objectTypeID);
                        const currentRoute = routeHistory.find(rh => rh.category === viewCategory) ?? getDefaultRoute(viewCategory);
                        const effectiveRoute = currentRoute?.parentViewID !== undefined
                            ? routes.find(nr => nr.viewID === currentRoute?.parentViewID) ?? currentRoute
                            : currentRoute;

                        return [
                            <BreadCrumbObjectItem
                                key={`object:${index}`}
                                item={item}
                                offline={offlineState.offline}
                                route={effectiveRoute as IRoute}
                                onClick={(
                                    item,
                                    route,
                                    shiftKey,
                                    ctrlKey
                                ) => {
                                    navigateToView(
                                        item.objectTypeID,
                                        item.objectID,
                                        route.viewID,
                                        NavigationSource.Breadcrumb,
                                        (shiftKey || ctrlKey) ? {
                                            newViewType: shiftKey ? NewViewTypeEnum.NewWindow : NewViewTypeEnum.NewTab
                                        } : undefined
                                    );
                                }}
                            />
                            ,
                            <BreadCrumbViewItem
                                key={`view:${index}`}
                                route={effectiveRoute as IRoute}
                                item={item}
                                customizationSettings={settings}
                                onClick={(
                                    item,
                                    route,
                                    shiftKey,
                                    ctrlKey
                                ) => {
                                    navigateToView(
                                        item.objectTypeID,
                                        item.objectID,
                                        route.viewID,
                                        NavigationSource.Breadcrumb,
                                        (shiftKey || ctrlKey) ? {
                                            newViewType: shiftKey ? NewViewTypeEnum.NewWindow : NewViewTypeEnum.NewTab
                                        } : undefined
                                    );
                                }}
                            />
                        ]
                    })
                }
            </Breadcrumbs>
        </Box>
    );
}
